/**
 * App root — routing + auth gating. Mirrors the Receipts App shell: an
 * AuthProvider wraps the routes, /login and /auth/callback are public, and
 * everything else is behind ProtectedRoute. A ToastProvider sits inside auth so
 * any authed page can raise transient confirmations (e.g. "Copied 6 codes").
 *
 * Live routes: /maps (Dashboard), /maps/:id (Editor), /datasets (Datasets
 * library), and /settings (Settings). The root path redirects to /maps.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Editor from './pages/Editor';
import Datasets from './pages/Datasets';
import Settings from './pages/Settings';
import './styles/global.css';

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#6B7280' }}>
      Loading…
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#6B7280', gap: '1rem', padding: '2rem', textAlign: 'center' }}>
      <div style={{ fontSize: '1.125rem', fontWeight: 500, color: '#1F2937' }}>Something went wrong</div>
      <div>{message}</div>
      <button
        onClick={() => window.location.reload()}
        style={{ marginTop: '0.5rem', padding: '0.5rem 1.5rem', background: '#0038EE', color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
      >
        Retry
      </button>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, authError } = useAuth();
  if (loading) return <LoadingScreen />;
  if (authError) return <ErrorScreen message={authError} />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { isAuthenticated, loading, authError } = useAuth();
  if (loading) return <LoadingScreen />;
  if (authError) return <ErrorScreen message={authError} />;

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/maps" replace /> : <Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/maps" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/maps/:id" element={<ProtectedRoute><Editor /></ProtectedRoute>} />
      <Route path="/datasets" element={<ProtectedRoute><Datasets /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/maps" replace />} />
      <Route path="*" element={<Navigate to="/maps" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </AuthProvider>
  );
}
